// Is this forecast COLLABORATIVE, and from which side?
//
// A separate axis from `forecastBadge.js`, deliberately. That badge occupies the
// row's one status slot and answers "what is this forecast DOING" — running,
// errored, resolved. Collaboration answers "who else can see this", which is
// true or false independently of every one of those states: a running forecast
// can be shared, a resolved one can be shared, and a row can be both at once. So
// this is an ADDITIONAL marker in the card's corner rather than a competitor for
// a slot that already has an occupant.
//
// TWO SIDES, NOT ONE FLAG. "I made this collaborative" and "someone made this
// collaborative with me" are different facts with different consequences — one is
// something you did and can undo, the other is something you received and cannot
// manage — so they read differently on the card.
//
// Pure: no React, no store.

import { ownerAttributionLabel } from "./sharedRowGuards.js";

// ONE GREY FOR BOTH, and the ARROW carries the distinction: out (↗) for "I am
// sharing this", in (↙) for "this was shared with me".
//
// They were two loud colours while the placement was being worked out, which read
// as a STATUS — the same visual language as the running/errored pills — when
// collaboration is a quiet property of the row that is true alongside every one of
// those states. Grey puts it back in its place. The consequence to respect: the
// arrow is now the only thing telling the two apart, so it has to stay legible at
// badge size rather than becoming a decorative flourish beside the figures.
//
// `direction` is what CollabIcon draws; `label` survives as the title/aria text,
// since an icon-only badge still has to say what it means to a screen reader and
// on hover.
export const COLLAB_BADGE_STYLES = {
  "shared-by-me": { label: "Sharing", direction: "out" },
  "shared-with-me": { label: "Shared with me", direction: "in" },
};

/**
 * @param {{isOwn?: boolean, isShared?: boolean}|null|undefined} f a forecast row
 * @returns {{kind: "shared-by-me"|"shared-with-me", label: string,
 *   direction: "in"|"out"}|null}
 *   null when the row is not collaborative at all — the common case, and the one
 *   that must render nothing rather than an empty badge.
 */
export function collabBadge(f) {
  if (!f) return null;
  // RECIPIENT FIRST. `isOwn === false` is set only by the shared lane, and the
  // shared wire deliberately DROPS `isShared` (a viewer must learn nothing about
  // other grants) — so checking ownership first is what keeps a viewer's row from
  // falling through to a missing flag and badging nothing.
  //
  // Strict `=== false`, never `!f.isOwn`: every owner-leg row predating the field
  // reads as owned, which is the fail-safe direction for a marker that would
  // otherwise tell an owner their own forecast was shared WITH them.
  if (f.isOwn === false)
    return { kind: "shared-with-me", ...COLLAB_BADGE_STYLES["shared-with-me"] };
  // The owner leg carries `isShared` — a bare boolean, never a count and never
  // the subjects, so the card discloses that the forecast is shared without
  // disclosing with whom.
  if (f.isShared === true) return { kind: "shared-by-me", ...COLLAB_BADGE_STYLES["shared-by-me"] };
  return null;
}

/**
 * Does this row carry a badge at all?
 *
 * Exists so a card can RESERVE ROOM for it: the badge is absolutely positioned in
 * the corner, so without an inset the question text runs underneath it. The title
 * is a two-line `-webkit-box` clamp, which rules out the usual float-spacer trick
 * (floats do not flow inside that box), so the card sets a right padding instead
 * and the text wraps sooner.
 *
 * A predicate rather than each row calling `collabBadge` and testing it: three
 * row variants need this and a fourth will, and the reason to inset is exactly
 * "the badge is there", not some other coincident fact.
 *
 * @param {{isOwn?: boolean, isShared?: boolean}|null|undefined} f
 * @returns {boolean}
 */
export const hasCollabBadge = (f) => collabBadge(f) !== null;

/**
 * What the badge says on hover — WHO, not just what.
 *
 * The owner's name used to sit on the card as small grey text next to the event
 * count. Off the card now and onto this hover: attribution is something a reader
 * wants ONCE, when they wonder where a row came from, and spending permanent
 * card width on it pushed the question text around on every shared row for a fact
 * most glances do not need.
 *
 * Reuses `ownerAttributionLabel` for the recipient side rather than re-deriving
 * name-then-email-then-fallback, so the wording cannot drift from what the rest of
 * the shared-row UI says. The owner side is a fixed sentence: there is no "by"
 * — it is them.
 *
 * @param {{isOwn?: boolean, isShared?: boolean, owner?: object}|null|undefined} f
 * @returns {string|null} null when the row carries no badge
 */
export function collabBadgeTitle(f) {
  const badge = collabBadge(f);
  if (!badge) return null;
  return badge.kind === "shared-by-me" ? "You've shared this forecast" : ownerAttributionLabel(f);
}
